import { motion } from 'framer-motion';
import { Lightbulb, LayoutGrid, Sparkles, Upload, BarChart3, RefreshCw, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const workflowSteps = [
  {
    id: 'idea',
    icon: Lightbulb,
    titleKey: 'landing.workflow.ideaTitle',
    descKey: 'landing.workflow.ideaDesc',
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    glowColor: 'rgba(16,185,129,0.15)',
  },
  {
    id: 'card',
    icon: LayoutGrid,
    titleKey: 'landing.workflow.cardTitle',
    descKey: 'landing.workflow.cardDesc',
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    borderColor: 'border-purple-200 dark:border-purple-800',
    iconBg: 'bg-purple-100 dark:bg-purple-900/40',
    iconColor: 'text-purple-600 dark:text-purple-400',
    glowColor: 'rgba(168,85,247,0.15)',
  },
  {
    id: 'score',
    icon: Sparkles,
    titleKey: 'landing.workflow.scoreTitle',
    descKey: 'landing.workflow.scoreDesc',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40',
    iconColor: 'text-amber-600 dark:text-amber-400',
    glowColor: 'rgba(245,158,11,0.15)',
  },
  {
    id: 'publish',
    icon: Upload,
    titleKey: 'landing.workflow.publishTitle',
    descKey: 'landing.workflow.publishDesc',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    iconColor: 'text-blue-600 dark:text-blue-400',
    glowColor: 'rgba(59,130,246,0.15)',
  },
  {
    id: 'analytics',
    icon: BarChart3,
    titleKey: 'landing.workflow.analyticsTitle',
    descKey: 'landing.workflow.analyticsDesc',
    color: 'from-rose-500 to-red-500',
    bgColor: 'bg-rose-50 dark:bg-rose-950/30',
    borderColor: 'border-rose-200 dark:border-rose-800',
    iconBg: 'bg-rose-100 dark:bg-rose-900/40',
    iconColor: 'text-rose-600 dark:text-rose-400',
    glowColor: 'rgba(244,63,94,0.15)',
  },
  {
    id: 'next',
    icon: RefreshCw,
    titleKey: 'landing.workflow.nextTitle',
    descKey: 'landing.workflow.nextDesc',
    color: 'from-indigo-500 to-violet-500',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/30',
    borderColor: 'border-indigo-200 dark:border-indigo-800',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    glowColor: 'rgba(99,102,241,0.15)',
  },
];

const cardVariants: any = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export function WorkflowShowcase() {
  const { t } = useTranslation();
  return (
    <section className="pt-8 pb-20 px-4 sm:px-6 lg:px-8 ">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.workflow.headingPrefix')} <span className="text-primary-600 dark:text-primary-400">{t('landing.workflow.headingHighlight')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t('landing.workflow.subtitle')}
          </p>
        </motion.div>

        {/* Workflow Steps */}
        <div className="relative mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  className="relative"
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                >
                  <motion.div
                    className={`${step.bgColor} rounded-2xl border ${step.borderColor} p-6 xl:p-5 h-full min-h-[210px] cursor-default`}
                    whileHover={{
                      y: -6,
                      boxShadow: `0 20px 40px ${step.glowColor}`,
                      transition: { duration: 0.25, ease: 'easeOut' },
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Step Number + Icon */}
                    <div className="flex items-center justify-between mb-4">
                      <motion.div
                        className={`w-10 h-10 rounded-xl ${step.iconBg} flex items-center justify-center`}
                        whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}
                      >
                        <Icon className={`w-5 h-5 ${step.iconColor}`} />
                      </motion.div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full bg-gradient-to-r ${step.color} text-white`}>
                        Step {index + 1}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 leading-snug">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {t(step.descKey)}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Comparison CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <motion.div 
            className="inline-flex flex-wrap items-center justify-center gap-2.5 px-6 py-3.5 bg-white/60 dark:bg-gray-900/40 backdrop-blur-md rounded-full border border-gray-200/60 dark:border-gray-700/50 shadow-[0_8px_30px_rgba(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.2)] max-w-full cursor-default"
            whileHover={{ scale: 1.02, y: -2 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mr-1">{t('landing.workflow.replace')}</span>
            {['Trello', 'ChatGPT', 'vidIQ', 'Buffer'].map((tool, i, arr) => (
              <span key={tool} className="flex items-center gap-2.5">
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{tool}</span>
                {i < arr.length - 1 && <span className="text-sm font-bold text-gray-400 dark:text-gray-500">+</span>}
              </span>
            ))}
            <div className="flex items-center gap-2.5 ml-2 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
              <span className="text-base font-extrabold bg-gradient-to-r from-primary-600 to-purple-600 dark:from-primary-400 dark:to-purple-400 bg-clip-text text-transparent drop-shadow-sm">PlanWise</span>
              <motion.div 
                animate={{ x: [0, 4, 0] }} 
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight className="w-4 h-4 text-primary-500" />
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
